import React, { useState, useEffect } from "react";
import Table from "@mui/material/Table";
import TableBody from "@mui/material/TableBody";
import TableCell from "@mui/material/TableCell";
import TableHead from "@mui/material/TableHead";
import TableRow from "@mui/material/TableRow";
import DragHandleIcon from "@mui/icons-material/DragHandle";
import { DragDropContext, Draggable, Droppable } from "react-beautiful-dnd";
import { TextField, TableFooter, Button } from "@mui/material";
import { toast } from "react-hot-toast";
import { TrashIcon } from "@heroicons/react/24/outline";
import { PMField } from "../../../../utils/PMField";
import { ResourceLoading } from "../../../../utils/ResourceLoading";
import { PMNotice } from "../../../../utils/notifications";
import ProConfirmation from "../../../../utils/ProConfirmation";
import { t } from "i18next";
import { TWclassNames } from "../../../../utils/Div";

const reorder = (list, startIndex, endIndex) => {
  const result = Array.from(list);
  const [removed] = result.splice(startIndex, 1);
  result.splice(endIndex, 0, removed);

  return result;
};

const grid = 8;

const getItemStyle = (isDragging, draggableStyle) => ({
  userSelect: "none",
  padding: 0,
  margin: `0 0 ${grid}px 0`,
  background: isDragging ? "lightgrey" : null,
  ...draggableStyle,
});

export default function HourlyCostAndRatesTable(props) {
  const { setFieldValue, values, isLoading, drag } = props;

  const name = props.name ? props.name : "user_hourly_cost_and_rates";

  const rows = values[name];

  const columns = [
    { id: "drag", label: "", minWidth: 10, maxWidth: 5, active: drag },
    {
      id: "valid_from",
      label: "Gyldig fra",
      minWidth: 100,
      active: true,
    },
    {
      id: "hourly_rate",
      label: "Timepris",
      minWidth: 100,
      align: "left",
      active: true,
    },
    {
      id: "budget_rate",
      label: "Budsjettpris",
      minWidth: 100,
      align: "left",
      active: true,
    },
    {
      id: "hour_cost_rate",
      label: "Timekostnad",
      minWidth: 100,
      align: "left",
      active: true,
    },
  ];

  const orderItems = (rows) => {
    var array = [];

    if (!rows) return;

    rows.map((row, i) => {
      const rowParams = {
        ...row,
        row_order: i,
        draggableId: "id" + row.id,
      };
      array.push(rowParams);
    });
    return array;
  };

  const defaultRows = [
    {
      id: null,
      row_order: null,
      valid_from: new Date().toISOString().split("T")[0],
      hourly_rate: 0,
      budget_rate: 0,
      hour_cost_rate: 0,
    },
  ];

  const [items, setItems] = useState(
    orderItems(rows?.length > 0 ? rows : defaultRows)
  );

  useEffect(() => {
    const filteredItems = items.filter((item) => item.is_editable !== false);
    setFieldValue(name, filteredItems);
  }, [items]);

  const addItemRow = (type) => {
    const newRow = {
      id: null,
      row_order: items.length,
      valid_from: new Date().toISOString().split("T")[0],
      hourly_rate: 0,
      budget_rate: 0,
      hour_cost_rate: 0,
      draggableId:
        "temp_" + Date.now() + "_" + Math.random().toString(36).substr(2, 9),
      is_new: true,
    };
    setItems((prevState) => [...prevState, newRow]);
  };

  const onItemRowChange = (e, data) => {
    if (typeof e === "function") {
      e.preventDefault();
    }

    let prevItems = [...items];
    let item = {
      ...prevItems[data?.row_order],
      [e.target.name]: e.target.value,
    };
    prevItems[data?.row_order] = item;
    setItems(prevItems);
  };

  const onDragEnd = (result) => {
    if (!result.destination) {
      return;
    }

    const newItems = reorder(
      items,
      result.source.index,
      result.destination.index
    );

    setItems(orderItems(newItems));
  };

  const [selectedItemRow, setSelectedItemRow] = useState();
  const [deleteConfirmationOpen, setDeleteConfirmationOpen] = useState(false);

  const handleDeleteRow = () => {
    setDeleteConfirmationOpen(false);
    if (selectedItemRow) {
      const newArray = items.map((item) =>
        item.id === selectedItemRow.id ||
        item.draggableId === selectedItemRow.draggableId
          ? { ...item, delete: true }
          : item
      );
      setItems(orderItems(newArray));
    }
  };

  const deleteRow = (item) => {
    setSelectedItemRow(item);
    setDeleteConfirmationOpen(true);
  };

  const itemRow = (item, provided, snapshot) => {
    if (item.delete) return null;
    return (
      <TableRow
        hover
        key={item.id}
        ref={provided.innerRef}
        {...provided.draggableProps}
        {...provided.dragHandleProps}
        style={getItemStyle(snapshot.isDragging, provided.draggableProps.style)}
      >
        {drag ? (
          <TableCell>
            <DragHandleIcon />
          </TableCell>
        ) : null}

        <TableCell align="left">
          <TextField
            required
            disabled={item?.is_editable === false}
            variant="standard"
            value={item?.valid_from}
            fullWidth
            type="date"
            name="valid_from"
            onChange={(e) => onItemRowChange(e, item)}
          />
        </TableCell>

        <TableCell align="left">
          <TextField
            disabled={item?.is_editable === false}
            variant="standard"
            value={item?.hourly_rate}
            fullWidth
            name="hourly_rate"
            type="number"
            step="0.01"
            onChange={(e) => onItemRowChange(e, item)}
          />
        </TableCell>

        <TableCell align="left">
          <TextField
            disabled={item?.is_editable === false}
            variant="standard"
            value={item?.budget_rate}
            fullWidth
            name="budget_rate"
            type="number"
            step="0.01"
            onChange={(e) => onItemRowChange(e, item)}
          />
        </TableCell>

        <TableCell align="left">
          <TextField
            disabled={item?.is_editable === false}
            variant="standard"
            value={item?.hour_cost_rate}
            fullWidth
            name="hour_cost_rate"
            type="number"
            step="0.01"
            onChange={(e) => onItemRowChange(e, item)}
          />
        </TableCell>

        <TableCell align="right">
          <TrashIcon
            className={TWclassNames(
              "cursor-pointer h-5 text-red-500 hover:text-red-700"
            )}
            onClick={() => deleteRow(item)}
          />
        </TableCell>
      </TableRow>
    );
  };

  if (isLoading) {
    return <ResourceLoading />;
  }

  return (
    <React.Fragment>
      <Table sx={{ width: "100%" }}>
        <TableHead>
          <TableRow>
            {columns.map((column) =>
              column.active ? (
                <TableCell
                  key={column.id}
                  align={column.align}
                  style={{
                    top: 57,
                    minWidth: column.minWidth,
                    maxWidth: column.maxWidth,
                    width: column.width,
                  }}
                >
                  {column.label}
                </TableCell>
              ) : null
            )}
          </TableRow>
        </TableHead>

        <DragDropContext onDragEnd={onDragEnd}>
          <Droppable droppableId="droppable">
            {(provided, snapshot) => (
              <TableBody {...provided.droppableProps} ref={provided.innerRef}>
                {items?.map((item, index) => (
                  <Draggable
                    key={item.id}
                    draggableId={item.draggableId}
                    index={index}
                  >
                    {(provided, snapshot) => itemRow(item, provided, snapshot)}
                  </Draggable>
                ))}
                {provided.placeholder}
              </TableBody>
            )}
          </Droppable>
        </DragDropContext>

        <TableFooter>
          <TableRow>
            <TableCell align="left" colSpan={2}>
              <Button onClick={() => addItemRow()}>Ny rad</Button>
            </TableCell>
            <TableCell align="left" colSpan={3}></TableCell>
          </TableRow>
        </TableFooter>
      </Table>

      <ProConfirmation
        severity="confirmation"
        open={deleteConfirmationOpen}
        message={"Er du sikker på at du vil slette denne raden?"}
        action="delete"
        module="user_hourly_cost_and_rate"
        yesCallback={handleDeleteRow}
        cancelCallback={() => setDeleteConfirmationOpen(false)}
      />
    </React.Fragment>
  );
}

HourlyCostAndRatesTable.defaultProps = {
  drag: true,
};
